"use client";

import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Upload, MapPin, Search, Plus, Trash2, Car, Footprints, Bike, Bus,
  Save, Link2, Download, Check, Route, Clock, Loader2, Play,
  Mountain, Layers, ChevronDown, ChevronUp, Users, User, Sliders,
  Map as MapIcon, AlertCircle, ListOrdered,
} from "lucide-react";
import type { UseGpxRouteReturn } from "./useGpxRoute";
import type { TravelMode, NearbyRouteItem, SavedGpxRoute } from "./types";
import WaypointList from "./WaypointList";
import RouteRangeSlider from "./RouteRangeSlider";
import ElevationProfile from "./ElevationProfile";
import { useTheme } from "@/features/theme";

// ── Constants ─────────────────────────────────────────────────────────────────

const TRAVEL_MODES: { mode: TravelMode; Icon: React.ElementType; label: string }[] = [
  { mode: "driving",   Icon: Car,        label: "Drive"   },
  { mode: "walking",   Icon: Footprints, label: "Walk"    },
  { mode: "bicycling", Icon: Bike,       label: "Bike"    },
  { mode: "transit",   Icon: Bus,        label: "Transit" },
];

const ROUTE_COLORS = [
  "#6366f1", // indigo
  "#ec4899", // pink
  "#f97316", // orange
  "#22c55e", // green
  "#06b6d4", // cyan
  "#eab308", // yellow
];

type Tab = "track" | "scenes" | "style";

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtDist(m: number): string {
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`;
}

function fmtTime(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface GpxRoutePanelProps extends UseGpxRouteReturn {
  isDark: boolean;
  isMobile?: boolean;
  onOpenDashboard?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function GpxRoutePanel({
  isDark: isDarkProp, isMobile,
  isOpen: _,
  close,
  gpxRoute, parseError,
  handleFileUpload,
  settings, updateSetting,
  displayTrkpts,
  waypoints, addWaypoint, removeWaypoint, reorderWaypoints,
  proximityResults, proximityLoading, proximityCount,
  searchProximity, addFromProximity, addAllProximityResults,
  isAddingMode, setIsAddingMode,
  savedRoutes, saveRoute, loadRoute, deleteRoute,
  savedFlash, shareCopied,
  exportGpx, copyShareLink,
  createTour, tourCreating,
  onOpenDashboard,
}: GpxRoutePanelProps) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  const [activeTab, setActiveTab] = useState<Tab>("track");
  const [showSaved, setShowSaved] = useState(false);
  const [hexInput, setHexInput] = useState(settings.routeColor);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Theme helpers ──────────────────────────────────────────────────────────
  const bg      ="bg-tone-950/90 border-ink/8 text-ink";
  const divider ="bg-ink/[0.06]";
  const sHead   ="text-tone-500";
  const inp     = isDark ? "bg-white/5 border-white/10 text-white placeholder-neutral-600 focus:border-indigo-500" : "bg-neutral-50 border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:border-indigo-400";
  const modeBtn = (active: boolean) =>
    active
      ? "bg-indigo-600 text-white border-indigo-500"
      :"bg-ink/5 border-ink/10 text-tone-300 hover:bg-ink/10";

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  const handleCreateTour = useCallback(async () => {
    await createTour((_colId) => {
      onOpenDashboard?.();
    });
  }, [createTour, onOpenDashboard]);

  // ── Tab content ────────────────────────────────────────────────────────────

  function renderTrackTab() {
    return (
      <div className="space-y-4">

        {/* File drop zone */}
        {!gpxRoute ? (
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className={`relative flex flex-col items-center gap-3 py-8 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${
              isDark ? "border-white/15 hover:border-indigo-500/60 text-neutral-500" : "border-neutral-300 hover:border-indigo-400/60 text-neutral-400"
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-7 h-7 opacity-50" />
            <div className="text-center">
              <p className="text-xs font-semibold">Drop a .gpx file here</p>
              <p className="text-[10px] mt-0.5 opacity-60">or click to browse — max 10 MB</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".gpx,application/gpx+xml"
              className="sr-only"
              onChange={(e) => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); }}
            />
          </div>
        ) : (
          <>
            {/* Stats badge */}
            <div className={`rounded-xl px-3 py-2.5 space-y-1.5 text-xs ${isDark ? "bg-indigo-500/10" : "bg-indigo-50"}`}>
              <div className="flex items-center justify-between">
                <span className={`font-black text-[10px] uppercase tracking-widest ${isDark ? "text-indigo-400" : "text-indigo-600"}`}>
                  Route Stats
                </span>
                <button
                  onClick={() => { fileInputRef.current?.click(); }}
                  className={`text-[10px] underline underline-offset-2 text-tone-500 hover:text-tone-300`}
                >
                  Replace
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Distance", value: fmtDist(gpxRoute.totalDistanceM) },
                  { label: "Gain", value: `+${fmtDist(gpxRoute.elevationGainM)}` },
                  { label: "Loss", value: `-${fmtDist(gpxRoute.elevationLossM)}` },
                ].map(({ label, value }) => (
                  <div key={label} className="text-center">
                    <p className={`text-[9px] uppercase tracking-wide font-black ${sHead}`}>{label}</p>
                    <p className={`font-bold mt-0.5 ${isDark ? "text-indigo-200" : "text-indigo-700"}`}>{value}</p>
                  </div>
                ))}
              </div>
              {gpxRoute.durationSec != null && (
                <p className={`text-[10px] text-center text-tone-500`}>
                  Recorded duration: {fmtTime(gpxRoute.durationSec)}
                </p>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept=".gpx,application/gpx+xml" className="sr-only"
              onChange={(e) => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); }} />
          </>
        )}

        {/* Parse error */}
        {parseError && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-red-500/10 text-red-400 text-xs">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {parseError}
          </div>
        )}

        {/* Trim section */}
        {gpxRoute && (
          <div>
            <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${sHead}`}>Trim Route</p>
            <RouteRangeSlider
              startFrac={settings.trimStart}
              endFrac={settings.trimEnd}
              totalDistanceM={gpxRoute.totalDistanceM}
              onChangeStart={(v) => updateSetting("trimStart", v)}
              onChangeEnd={(v) => updateSetting("trimEnd", v)}
              isDark={isDark}
            />
          </div>
        )}

        {/* Simplify section */}
        {gpxRoute && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className={`text-[10px] font-black uppercase tracking-widest ${sHead}`}>Simplify</p>
              <span className={`text-[10px] font-mono text-tone-400`}>
                {settings.simplifyTolerance === 0 ? "Off" : `~${Math.round(settings.simplifyTolerance * 111000)} m`}
              </span>
            </div>
            <input
              type="range"
              min={0} max={0.0003} step={0.000005}
              value={settings.simplifyTolerance}
              onChange={(e) => updateSetting("simplifyTolerance", parseFloat(e.target.value))}
              className="w-full accent-indigo-500"
            />
            <div className={`flex justify-between text-[9px] mt-0.5 ${sHead}`}>
              <span>Off</span>
              <span>{displayTrkpts.length} pts</span>
              <span>~30 m</span>
            </div>
          </div>
        )}

        {/* Proximity search */}
        {gpxRoute && (
          <div>
            <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${sHead}`}>Find Nearby Photos</p>
            <div className="space-y-2">
              {/* My photos toggle */}
              <label className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                settings.myPhotosEnabled
                  ? isDark ? "border-indigo-500/40 bg-indigo-500/10" : "border-indigo-300 bg-indigo-50"
                  :"border-ink/8 hover:bg-ink/5"
              }`}>
                <div className="flex items-center gap-2">
                  <User className={`w-3.5 h-3.5 ${settings.myPhotosEnabled ? "text-indigo-400" :"text-tone-500"}`} />
                  <span className="text-xs font-semibold">My Photos</span>
                </div>
                <input type="checkbox" checked={settings.myPhotosEnabled}
                  onChange={(e) => updateSetting("myPhotosEnabled", e.target.checked)} className="sr-only" />
                <div className={`w-8 h-4.5 rounded-full transition-colors relative ${settings.myPhotosEnabled ? "bg-indigo-500" :"bg-ink/15"}`}>
                  <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${settings.myPhotosEnabled ? "translate-x-3.5" : "translate-x-0.5"}`} />
                </div>
              </label>

              {/* Community toggle */}
              <label className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                settings.communityPhotosEnabled
                  ? isDark ? "border-indigo-500/40 bg-indigo-500/10" : "border-indigo-300 bg-indigo-50"
                  :"border-ink/8 hover:bg-ink/5"
              }`}>
                <div className="flex items-center gap-2">
                  <Users className={`w-3.5 h-3.5 ${settings.communityPhotosEnabled ? "text-indigo-400" :"text-tone-500"}`} />
                  <span className="text-xs font-semibold">Community Photos</span>
                </div>
                <input type="checkbox" checked={settings.communityPhotosEnabled}
                  onChange={(e) => updateSetting("communityPhotosEnabled", e.target.checked)} className="sr-only" />
                <div className={`w-8 h-4.5 rounded-full transition-colors relative ${settings.communityPhotosEnabled ? "bg-indigo-500" :"bg-ink/15"}`}>
                  <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${settings.communityPhotosEnabled ? "translate-x-3.5" : "translate-x-0.5"}`} />
                </div>
              </label>

              {/* Radius slider */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] ${sHead}`}>Search radius</span>
                  <span className={`text-[10px] font-mono font-semibold text-tone-300`}>{settings.proximityRadiusM} m</span>
                </div>
                <input
                  type="range" min={5} max={500} step={5}
                  value={settings.proximityRadiusM}
                  onChange={(e) => updateSetting("proximityRadiusM", parseInt(e.target.value))}
                  className="w-full accent-indigo-500"
                />
                <div className={`flex justify-between text-[9px] mt-0.5 ${sHead}`}>
                  <span>5 m</span><span>500 m</span>
                </div>
              </div>

              {/* Search button */}
              <button
                onClick={async () => {
                  await searchProximity();
                  setActiveTab("scenes");
                }}
                disabled={proximityLoading}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-xs font-bold transition-colors"
              >
                {proximityLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                {proximityLoading ? "Searching…" : "Search Photos"}
              </button>

              {/* Result count badge */}
              {proximityCount !== null && !proximityLoading && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold ${
                  proximityCount > 0
                    ? isDark ? "bg-indigo-500/15 text-indigo-300" : "bg-indigo-50 text-indigo-700"
                    :"bg-ink/5 text-tone-400"
                }`}>
                  <Search className="w-3 h-3 shrink-0" />
                  {proximityCount > 0
                    ? `${proximityCount} photo${proximityCount === 1 ? "" : "s"} found — added to Scenes`
                    : "No photos found near this route"}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderScenesTab() {
    return (
      <div className="space-y-4">
        {/* Waypoints list */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className={`text-[10px] font-black uppercase tracking-widest ${sHead}`}>
              Scenes ({waypoints.length})
            </p>
            {waypoints.length > 0 && (
              <button
                onClick={() => waypoints.forEach((w) => removeWaypoint(w.panoId))}
                className="text-[10px] text-red-400 hover:text-red-300 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
          {waypoints.length === 0 ? (
            <div className={`flex flex-col items-center gap-2 py-6 rounded-xl border border-dashed border-ink/10 text-tone-600`}>
              <MapPin className="w-6 h-6 opacity-30" />
              <p className="text-xs text-center">No scenes yet — search nearby photos<br/>or click map to add</p>
            </div>
          ) : (
            <WaypointList
              waypoints={waypoints}
              onReorder={reorderWaypoints}
              onRemove={removeWaypoint}
              isDark={isDark}
            />
          )}
        </div>

        {/* Add from map */}
        <button
          onClick={() => setIsAddingMode(!isAddingMode)}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all ${
            isAddingMode
              ? "bg-indigo-600 border-indigo-500 text-white"
              :"border-ink/10 text-tone-300 hover:bg-ink/5"
          }`}
        >
          <MapPin className="w-3.5 h-3.5" />
          {isAddingMode ? "Click a panorama on the map…" : "Add from map"}
        </button>

        {/* Create Tour */}
        <button
          onClick={handleCreateTour}
          disabled={waypoints.length === 0 || tourCreating}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-xs font-bold transition-colors"
        >
          {tourCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          {tourCreating ? "Creating Tour…" : "Create Tour from Route"}
        </button>
      </div>
    );
  }

  function renderStyleTab() {
    return (
      <div className="space-y-4">
        {/* Route name */}
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${sHead}`}>Route Name</p>
          <input
            type="text"
            value={settings.routeName}
            onChange={(e) => updateSetting("routeName", e.target.value)}
            placeholder="Route name…"
            className={`w-full rounded-xl px-3 py-2 text-sm focus:outline-none border transition-colors ${inp}`}
          />
        </div>

        {/* Description */}
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${sHead}`}>Description</p>
          <textarea
            rows={2}
            value={settings.description}
            onChange={(e) => updateSetting("description", e.target.value)}
            placeholder="Optional description…"
            className={`w-full rounded-xl px-3 py-2 text-sm focus:outline-none border resize-none transition-colors ${inp}`}
          />
        </div>

        {/* Travel mode */}
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${sHead}`}>Travel Mode</p>
          <div className="grid grid-cols-4 gap-1.5">
            {TRAVEL_MODES.map(({ mode, Icon, label }) => (
              <button
                key={mode}
                onClick={() => updateSetting("travelMode", mode)}
                className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-[10px] font-bold transition-all ${modeBtn(settings.travelMode === mode)}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Route color */}
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${sHead}`}>Route Color</p>
          <div className="flex items-center gap-2 flex-wrap">
            {ROUTE_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { updateSetting("routeColor", c); setHexInput(c); }}
                className={`w-7 h-7 rounded-full border-2 transition-all ${settings.routeColor === c ? "border-white scale-110" : "border-transparent"}`}
                style={{ background: c }}
              />
            ))}
            <input
              type="text"
              value={hexInput}
              maxLength={7}
              onChange={(e) => {
                setHexInput(e.target.value);
                if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) updateSetting("routeColor", e.target.value);
              }}
              className={`flex-1 min-w-0 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none border ${inp}`}
              placeholder="#6366f1"
            />
          </div>
        </div>

        {/* Line width */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className={`text-[10px] font-black uppercase tracking-widest ${sHead}`}>Line Width</p>
            <span className={`text-[10px] font-mono text-tone-400`}>{settings.routeWidth} px</span>
          </div>
          <input
            type="range" min={2} max={8} step={0.5}
            value={settings.routeWidth}
            onChange={(e) => updateSetting("routeWidth", parseFloat(e.target.value))}
            className="w-full accent-indigo-500"
          />
        </div>

        {/* Dashed toggle */}
        <label className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border cursor-pointer border-ink/8`}>
          <span className="text-xs font-semibold">Dashed line</span>
          <input type="checkbox" checked={settings.routeDash}
            onChange={(e) => updateSetting("routeDash", e.target.checked)} className="sr-only" />
          <div className={`w-8 h-4.5 rounded-full transition-colors relative ${settings.routeDash ? "bg-indigo-500" :"bg-ink/15"}`}>
            <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${settings.routeDash ? "translate-x-3.5" : "translate-x-0.5"}`} />
          </div>
        </label>

        {/* Elevation toggle */}
        <label className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border cursor-pointer border-ink/8`}>
          <div className="flex items-center gap-2">
            <Mountain className="w-3.5 h-3.5 text-tone-500" />
            <span className="text-xs font-semibold">Show elevation</span>
          </div>
          <input type="checkbox" checked={settings.showElevation}
            onChange={(e) => updateSetting("showElevation", e.target.checked)} className="sr-only" />
          <div className={`w-8 h-4.5 rounded-full transition-colors relative ${settings.showElevation ? "bg-indigo-500" :"bg-ink/15"}`}>
            <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${settings.showElevation ? "translate-x-3.5" : "translate-x-0.5"}`} />
          </div>
        </label>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <motion.div
      initial={isMobile ? { y: "100%" } : { opacity: 0, x: -10, scale: 0.97 }}
      animate={isMobile ? { y: 0 } : { opacity: 1, x: 0, scale: 1 }}
      exit={isMobile ? { y: "100%" } : { opacity: 0, x: -10, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 340, damping: 30 }}
      className={`${isMobile
        ? "fixed bottom-0 inset-x-0 z-[46] backdrop-blur-2xl border-t rounded-t-3xl shadow-2xl flex flex-col max-h-[88vh]"
        : "w-80 backdrop-blur-2xl border rounded-3xl shadow-2xl flex flex-col max-h-[86vh]"} ${bg}`}
    >
      {/* Mobile drag handle */}
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className={`w-8 h-1 rounded-full bg-ink/20`} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 shrink-0">
        <Route className="w-4 h-4 text-indigo-400 shrink-0" />
        <span className="font-black text-[11px] uppercase tracking-widest flex-1">GPX Route</span>
        {/* Saved routes toggle */}
        <button
          onClick={() => setShowSaved((s) => !s)}
          title="Saved routes"
          className={`p-1.5 rounded-full transition-all bg-ink/5 hover:bg-ink/10 text-ink/40 hover:text-ink`}
        >
          <ListOrdered className="w-3 h-3" />
        </button>
        <button
          onClick={close}
          className={`p-1.5 rounded-full transition-all bg-ink/5 hover:bg-ink/10 text-ink/40 hover:text-ink`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <div className={`h-px mx-4 ${divider}`} />

      {/* Saved routes panel */}
      <AnimatePresence>
        {showSaved && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden shrink-0"
          >
            <div className="px-4 py-3 space-y-1.5 max-h-40 overflow-y-auto">
              <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${sHead}`}>Saved Routes ({savedRoutes.length})</p>
              {savedRoutes.length === 0 ? (
                <p className={`text-xs text-tone-600`}>No saved routes yet.</p>
              ) : savedRoutes.map((r) => (
                <div key={r.id} className={`flex items-center gap-2 px-2.5 py-2 rounded-xl border border-ink/5 bg-ink/[0.02]`}>
                  <button onClick={() => { loadRoute(r); setShowSaved(false); }} className="flex-1 text-left">
                    <p className="text-xs font-semibold truncate">{r.name}</p>
                    <p className={`text-[10px] ${sHead}`}>{new Date(r.createdAt).toLocaleDateString()}</p>
                  </button>
                  <button onClick={() => deleteRoute(r.id)} className="p-1 rounded text-tone-600 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className={`h-px mx-4 ${divider}`} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <div className="flex gap-1 px-4 pt-3 pb-2 shrink-0">
        {(["track", "scenes", "style"] as Tab[]).map((tab) => {
          const icons: Record<Tab, React.ElementType> = { track: MapIcon, scenes: ListOrdered, style: Sliders };
          const labels: Record<Tab, string> = { track: "Track", scenes: "Scenes", style: "Style" };
          const Icon = icons[tab];
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${
                activeTab === tab
                  ?"bg-indigo-600 text-white"
                  :"text-tone-500 hover:text-tone-300 hover:bg-ink/5"
              }`}
            >
              <Icon className="w-3 h-3" />
              {labels[tab]}
            </button>
          );
        })}
      </div>

      <div className={`h-px mx-4 ${divider}`} />

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3 space-y-4">
        {activeTab === "track"  && renderTrackTab()}
        {activeTab === "scenes" && renderScenesTab()}
        {activeTab === "style"  && renderStyleTab()}

        {/* Elevation profile */}
        {gpxRoute && settings.showElevation && (
          <ElevationProfile
            trkpts={gpxRoute.trkpts}
            displayTrkpts={displayTrkpts}
            trimStart={settings.trimStart}
            trimEnd={settings.trimEnd}
            isDark={isDark}
          />
        )}
      </div>

      {/* Footer actions */}
      <div className={`px-4 pb-4 pt-3 shrink-0 border-t border-ink/[0.06]`}>
        <div className="flex gap-2">
          <button
            onClick={saveRoute}
            disabled={!gpxRoute}
            title="Save route"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-30 ${
              savedFlash
                ? "bg-green-600 border-green-500 text-white"
                :"border-ink/10 text-tone-300 hover:bg-ink/5"
            }`}
          >
            {savedFlash ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
            {savedFlash ? "Saved!" : "Save"}
          </button>
          <button
            onClick={copyShareLink}
            disabled={!gpxRoute}
            title="Copy share link"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-30 ${
              shareCopied
                ? "bg-green-600 border-green-500 text-white"
                :"border-ink/10 text-tone-300 hover:bg-ink/5"
            }`}
          >
            {shareCopied ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
            {shareCopied ? "Copied!" : "Share"}
          </button>
          <button
            onClick={exportGpx}
            disabled={!gpxRoute}
            title="Export GPX"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-30 ${
              isDark ? "border-white/10 text-neutral-300 hover:bg-white/5" : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
            }`}
          >
            <Download className="w-3 h-3" />
            Export
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Proximity result row ──────────────────────────────────────────────────────

function ProximityResultRow({
  item, already, onAdd, isDark: isDarkProp,
}: {
  item: NearbyRouteItem;
  already: boolean;
  onAdd: () => void;
  isDark: boolean;
}) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  const thumb = item.base_tile_url ? `${item.base_tile_url}9/0_0.webp` : null;
  return (
    <div className={`flex items-center gap-2 p-1.5 rounded-lg border transition-colors ${
      already
        ? isDark ? "border-indigo-500/20 bg-indigo-500/5 opacity-50" : "border-indigo-200 bg-indigo-50/50 opacity-50"
        :"border-ink/5 hover:bg-ink/5"
    }`}>
      <div className={`w-8 h-6 rounded-md overflow-hidden shrink-0 bg-tone-800`}>
        {thumb
          ? <img src={thumb} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><MapPin className="w-2.5 h-2.5 text-tone-600" /></div>
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold truncate">{item.name || "Untitled"}</p>
        <p className={`text-[9px] text-tone-600`}>{Math.round(item.dist_m)} m off route</p>
      </div>
      <button
        onClick={onAdd}
        disabled={already}
        className={`p-1 rounded-lg transition-colors ${already ? "opacity-0" : "text-indigo-400 hover:bg-indigo-500/10"}`}
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
